import React, { useCallback, useEffect, useState } from 'react';
// components
import AuthenticatedUserView from 'refactored/ui/views/authenticatedUserView';
import { useDispatch, useSelector } from 'react-redux';
import { push } from 'connected-react-router';
import routes from 'utils/routes';
import { userIsAuthenticatedRedir } from 'components/auth/hoc';
import PageLayout from 'refactored/features/emergencies/ui/page/pageLayout';
import { selectors as authSelectors } from 'state/auth';
import api from 'api';
import useSWR from 'swr';
import BasicLoader from 'refactored/features/emergencies/ui/loaders/basic-loader';
import TicketFormContainer from 'refactored/features/emergencies/ui/containers/ticketFormContainer';
import { CustomSticky } from 'refactored/features/emergencies/ui/sticky/CustomSticky';
import { Half } from 'refactored/features/emergencies/components/ticket-form/components/Half';
import { FormButton } from 'refactored/features/emergencies/ui/buttons/FormButton';
import PremiumSectionContainer from 'refactored/features/emergencies/components/ticket-form/components/PremiumSectionContainer';
import PremiumSection from 'refactored/features/emergencies/components/ticket-form/components/PremiumSection';
import FooterButtonContainer from 'refactored/features/emergencies/components/ticket-form/components/FooterButtonContainer';
import SectionTitle from 'refactored/features/emergencies/components/ticket-form/components/SectionTitle';
import { SectionContainer } from 'refactored/features/emergencies/components/ticket-form/components/SectionContainer';
import { SectionBody } from 'refactored/features/emergencies/components/ticket-form/components/SectionBody';
import { SectionLine } from 'refactored/features/emergencies/components/ticket-form/components/SectionLine';
import styled from 'styled-components';
import { PremiumInput } from 'refactored/features/emergencies/ui/inputs/customInput';
import { RadioButton } from 'refactored/features/emergencies/ui/inputs/ui/RadioButton';
import { RadioContainer } from 'refactored/features/emergencies/components/ticket-form/components/RadioContainer';
import { PhoneNumberUtil } from 'google-libphonenumber';
import { toast } from 'sonner';
import { ReactComponent as WhatsappIcon } from 'assets/icons/whatsapp.svg';
import { ReactComponent as EmailIcon } from 'assets/icons/email.svg';
import { useTranslation } from 'react-i18next';
import { PhoneInput } from './phone-input';

const phoneUtil = PhoneNumberUtil.getInstance();

export function validatePhoneNumber(phoneNumberString) {
  let isValid = false;

  try {
    const internationalPhoneNumber = phoneUtil.parse(phoneNumberString);

    if (phoneUtil.isValidNumber(internationalPhoneNumber)) {
      isValid = true;
    }
  } catch (error) {
    isValid = false;
  }

  return isValid;
}

const SectionTitleStyled = styled(SectionTitle)`
  font-size: 24px;
`;

const SectionTitleError = styled(SectionTitle)`
  color: red;
`;

const BasicLoaderWrapper = styled.div`
  padding: 32px;
  margin-left: auto;
  margin-right: auto;
  transform: scale(2);
`;

const SectionLineStyled = styled.div`
  &:first-of-type {
    border-top: 1px solid var(--grey300);
  }
  border-bottom: 1px solid var(--grey300);
  display: flex;
  align-items: center;
  width: 100%;
  padding-left: 16px;
  padding-top: 12px;
  padding-bottom: 12px;
  color: var(--grey700);
  font-size: 14px;
`;

const SectionLineLabel = styled.div`
  width: 200px;

  font-weight: 500;
`;

const SectionLineContent = styled.div`
  flex: 1;
`;

const SectionBodyStyled = styled(SectionBody)`
  gap: 0;
  margin-top: 8px;
`;

const defaultFormState = {
  language: 'en-GB',
  preferred_channel: 'email',
  email: '',
  whatsapp: '',
  hasEmailError: false,
  hasWhatsappError: false,
};

const availableCommunicationLanguages = [
  {
    value: 'en-GB',
    label: 'english',
  },
  {
    value: 'fr-FR',
    label: 'french',
  },
  {
    value: 'it-IT',
    label: 'italian',
  },
  {
    value: 'pl-PL',
    label: 'polish',
  },
  {
    value: 'ro-RO',
    label: 'romanian',
  },
  {
    value: 'es-ES',
    label: 'spanish',
  },
  {
    value: 'pt-PT',
    label: 'portuguese',
  },
];

const emailRegex =
  /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/;

const getUserDetailApi = async (userId) => {
  const response = await api.get(`users/${userId}/`);
  return response.data;
};

const saveUserDetailApi = async (userId, dto) => {
  const response = await api.patch(`users/${userId}/`, dto);
  return response;
};

const useProfilePageHook = (userId) => {
  const { t } = useTranslation('Profile');
  const { data, isLoading, error } = useSWR(`/users/${userId}`, () =>
    getUserDetailApi(userId)
  );

  const [formState, setFormState] = useState(defaultFormState);

  useEffect(() => {
    if (data) {
      const formStateWithExistingUserData = { ...defaultFormState };
      formStateWithExistingUserData.language = data.language;
      const preferredChannel = data?.communication_preferences?.channels?.find(
        (channel) => channel.preferred
      );
      if (preferredChannel) {
        formStateWithExistingUserData.preferred_channel = preferredChannel.type;
      }
      const emailChannel = data?.communication_preferences?.channels?.find(
        (channel) => channel.type === 'email'
      );
      if (emailChannel) {
        formStateWithExistingUserData.email = emailChannel.content;
      }
      const whatsappChannel = data?.communication_preferences?.channels?.find(
        (channel) => channel.type === 'whatsapp'
      );
      if (whatsappChannel) {
        formStateWithExistingUserData.whatsapp = whatsappChannel.content;
      }
      setFormState(formStateWithExistingUserData);
    }
  }, [data]);

  const hasEmailError = !new RegExp(emailRegex).test(formState.email);
  const hasWhatsappError = !validatePhoneNumber(formState.whatsapp);

  const [isSaving, setIsSaving] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const hasErrors =
    (formState.preferred_channel === 'email' && hasEmailError) ||
    (formState.preferred_channel === 'whatsapp' && hasWhatsappError);

  const phoneInputLanguage = data?.language.split('-')[0] || 'en';

  const onSave = useCallback(async () => {
    try {
      setShowErrors(true);
      if (hasErrors) {
        return;
      }
      setIsSaving(true);
      const dto = {
        language: formState.language,
        communication_preferences: {
          language: formState.language,
          channels: [],
        },
      };

      if (!hasEmailError) {
        dto.communication_preferences.channels.push({
          type: 'email',
          preferred: formState.preferred_channel === 'email',
          content: formState.email,
        });
      }

      if (!hasWhatsappError) {
        dto.communication_preferences.channels.push({
          type: 'whatsapp',
          preferred: formState.preferred_channel === 'whatsapp',
          content: formState.whatsapp,
        });
      }

      await saveUserDetailApi(userId, dto);
      toast.success(t('success'));

      const hasNewLanguage = data.language !== formState.language;
      if (hasNewLanguage) {
        window.location.reload();
      }
    } catch (e) {
      toast.error(t('error'));
    } finally {
      setIsSaving(false);
    }
  }, [formState, hasErrors, userId, data, hasEmailError, hasWhatsappError, t]);

  const setPreferredChannel = useCallback(
    (preferredChannel) => {
      if (isSaving) return;
      setFormState({
        ...formState,
        preferred_channel: preferredChannel,
      });
    },
    [formState, isSaving]
  );

  const setLanguage = useCallback(
    (language) => {
      setFormState({
        ...formState,
        language: language,
      });
    },
    [formState]
  );

  const setEmail = useCallback(
    (email) => {
      setFormState({
        ...formState,
        email: email,
      });
    },
    [formState]
  );

  const setWhatsapp = useCallback(
    (whatsapp) => {
      setFormState({
        ...formState,
        whatsapp: whatsapp,
      });
    },
    [formState]
  );

  return {
    formState,
    isLoading,
    error,
    isSaving,
    setPreferredChannel,
    setLanguage,
    onSave,
    setEmail,
    setWhatsapp,
    hasEmailError,
    hasWhatsappError,
    showErrors,
    phoneInputLanguage,
  };
};

const ProfilePage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation('Profile');

  const handleBack = () => {
    dispatch(push(`${routes.emergencies}/`));
  };

  const { ...user } = useSelector((state) => authSelectors.getUser(state));
  const shipper = useSelector((state) => authSelectors.getShipper(state));

  const userId = user.id;
  const userName = user.name;
  const userCompanyName = shipper.company_name;

  const {
    formState,
    setPreferredChannel,
    isLoading,
    error,
    isSaving,
    onSave,
    setLanguage,
    setEmail,
    setWhatsapp,
    hasEmailError,
    hasWhatsappError,
    showErrors,
    phoneInputLanguage,
  } = useProfilePageHook(userId);

  const className = '';

  const onCancel = () => {
    dispatch(push(`${routes.emergencies}/`));
  };

  const availableCommunicationLanguagesTranslated =
    availableCommunicationLanguages.map((language) => {
      return {
        value: language.value,
        label: t(`languages.${language.label}`),
      };
    });

  return (
    <AuthenticatedUserView className={className} handleBack={handleBack}>
      <PageLayout hideToolbar>
        <TicketFormContainer>
          {/* body */}
          <PremiumSectionContainer>
            <PremiumSection>
              <SectionContainer>
                <SectionTitleStyled>
                  {t('user_profile_title')}
                </SectionTitleStyled>
              </SectionContainer>
              {(() => {
                if (error) {
                  return (
                    <SectionContainer>
                      <SectionTitleError>
                        {t('loading_error')}
                      </SectionTitleError>
                    </SectionContainer>
                  );
                }
                if (isLoading) {
                  return (
                    <BasicLoaderWrapper>
                      <BasicLoader />
                    </BasicLoaderWrapper>
                  );
                }
                return (
                  <>
                    <SectionContainer>
                      <SectionTitle>{t('user_section_title')}</SectionTitle>
                      <SectionBodyStyled>
                        <SectionLineStyled>
                          <SectionLineLabel>{t('name')}</SectionLineLabel>
                          <SectionLineContent>{userName}</SectionLineContent>
                        </SectionLineStyled>
                        <SectionLineStyled>
                          <SectionLineLabel>{t('company')}</SectionLineLabel>
                          <SectionLineContent>
                            {userCompanyName}
                          </SectionLineContent>
                        </SectionLineStyled>
                      </SectionBodyStyled>
                    </SectionContainer>
                    <SectionContainer>
                      <SectionTitle>
                        {t('communication_preferences')}
                      </SectionTitle>
                      <SectionBodyStyled>
                        <SectionLine>
                          {/* preferred channel*/}
                          <RadioContainer>
                            <RadioButton
                              checked={formState.preferred_channel === 'email'}
                              onClick={() => {
                                setPreferredChannel('email');
                              }}
                            >
                              <EmailIcon />
                            </RadioButton>
                            <RadioButton
                              checked={
                                formState.preferred_channel === 'whatsapp'
                              }
                              onClick={() => {
                                setPreferredChannel('whatsapp');
                              }}
                            >
                              <WhatsappIcon />
                            </RadioButton>
                          </RadioContainer>

                          {/* channel content */}
                          {formState.preferred_channel === 'email' ? (
                            <PremiumInput
                              value={formState.email}
                              label={t('email')}
                              hasError={showErrors && hasEmailError}
                              name="profile.communication_preferences.channels.email.content"
                              type={'text'}
                              isRequired={false}
                              isDisabled={isSaving}
                              width={'290px'}
                              lang={phoneInputLanguage}
                              onChange={(event) => {
                                const value = event.target.value;
                                setEmail(value);
                              }}
                            />
                          ) : (
                            <PhoneInput
                              value={formState.whatsapp}
                              label={t('whatsapp')}
                              hasError={showErrors && hasWhatsappError}
                              name="profile.communication_preferences.channels.whatsapp.content"
                              width={'290px'}
                              lang={phoneInputLanguage}
                              isDisabled={isSaving}
                              handleChangeValue={(name, value) => {
                                setWhatsapp(value);
                              }}
                            />
                          )}

                          {/* language */}
                          <PremiumInput
                            value={formState.language}
                            label={t('language')}
                            showErrors={false}
                            name="profile.language"
                            type={'select'}
                            isRequired={true}
                            isDisabled={isSaving}
                            width={'160px'}
                            availableOptions={
                              availableCommunicationLanguagesTranslated
                            }
                            onChange={(value) => {
                              setLanguage(value);
                            }}
                          />
                        </SectionLine>
                        <SectionLine></SectionLine>
                      </SectionBodyStyled>
                    </SectionContainer>
                  </>
                );
              })()}
            </PremiumSection>
          </PremiumSectionContainer>
          {/* footer */}
          {!error && !isLoading && (
            <CustomSticky>
              <FooterButtonContainer>
                <Half>
                  <FormButton variant="GHOST" onClick={onCancel}>
                    {t('cancel')}
                  </FormButton>
                </Half>
                <Half>
                  <FormButton isDisabled={isSaving} onClick={onSave}>
                    {t('save')}
                  </FormButton>
                </Half>
              </FooterButtonContainer>
            </CustomSticky>
          )}
        </TicketFormContainer>
      </PageLayout>
    </AuthenticatedUserView>
  );
};

export default userIsAuthenticatedRedir(ProfilePage);
